import React, { FC, useEffect } from 'react';
import Icon from '@minecraft.icon';
import {
  BasicContainer,
  BodyText,
  BodyTextSmall,
  ColorProp,
  CustomBorderProps,
  FlexCol,
  FlexRow,
  H3Text,
  ImageButton,
  PrimaryButton,
  GridContainer,
  H4Text,
  Ribbon,
} from '@minecraft.atoms';
import { FormattedPriceMonth, useTranslation } from '@blocs.i18n';
import { PlanType } from '../usePlans';
import { BillingCodes, PlanCode } from '../enums/plans';
import { PAYWALL_STEPS } from '../enums/paywall';
import { PlanSelectionProps } from './types';
import { FeatureList } from './FeatureList';
import { PlanPriceSection } from './PlanPriceSection';
import { PlanButton } from './PlanButton';
import { generateUniqueId } from '../helpers/functions';
import { UpgradeModalExperimentGroups } from '../enums/experimentGroups';

export type PlanSmItemProps = {
  selectedPlan: PlanType;
  plan: PlanType;
  setSelectedPlan: (plan: PlanType) => void;
  countryCode: string;
  isAnnual: boolean;
  title: string;
  color?: ColorProp;
  modalVariant?: UpgradeModalExperimentGroups;
};

export const PlanSmItem: FC<PlanSmItemProps> = ({
  selectedPlan,
  plan,
  setSelectedPlan,
  countryCode,
  isAnnual,
  title,
  color = 'default',
  modalVariant,
}) => {
  const { t } = useTranslation();
  const isSelected = selectedPlan?.billingCode === plan.billingCode;
  const isCurrent = plan?.isCurrent && !plan?.canRenew;
  const isHotTrial = plan.isTrialPeriodEnabled && plan.isEssentialTrialPlan;
  const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB;

  let bgColor;
  if (isSelected) {
    bgColor = isLifestyleUpgradeModal ? `${plan.temperature}PaywallSuccessBg` : `${plan.temperature}PaywallDarkBg`;
  } else if (isCurrent) {
    bgColor = 'paywallVanilla';
  } else {
    bgColor = 'transparent';
  }
  const customBorder = {
    borderColor: `selectedItem`,
    borderSize: 0.1,
    borderStyle: 'solid',
  } as CustomBorderProps;

  return (
    <FlexCol
      w="full"
      mb="2"
      className="cn_h-full cn_relative"
      justifyContent="flex-end"
      alignItems="flex-end"
      bgColor={isSelected ? bgColor : 'transparent'}
      borderRadius="md"
      id={plan.billingCode}
      key={plan.billingCode}
      data-testid={`plan-option-container-${plan.billingCode}`}
      customBorders={
        !isLifestyleUpgradeModal
          ? {
              top: {
                borderColor: isHotTrial ? `${plan.temperature}TrialUpsellPaywall` : 'selectedItem',
                borderSize: isHotTrial ? 2 : 0.1,
                borderStyle: 'solid',
              } as CustomBorderProps,
              left: customBorder,
              right: customBorder,
              bottom: customBorder,
            }
          : undefined
      }
    >
      <ImageButton onClick={() => setSelectedPlan(plan)} unsetSizing className="cn_w-full cn_atom_p-0">
        {isHotTrial && (
          <BasicContainer w="full" className="cn_absolute text-center" top={-1.7} left={0.1}>
            <BodyText color="inverted" align="center" fontWeight="medium" className="cn_w-full text-center">
              {t(`common:paywall.plan.trial.upsellMessage`, { days: plan.trialLengthDays })}
            </BodyText>
          </BasicContainer>
        )}
        <FlexRow
          justifyContent="space-between"
          alignItems="flex-end"
          w="full"
          className="cn_relative cn_atom_px-2 cn_atom_py-4"
        >
          <FlexCol alignItems="center" justifyContent="center" gap="1">
            <Icon name={plan.icon} themeColor={color === 'default' ? 'vanillaPaywall' : 'inverted'} />{' '}
            <H3Text className="cn_text-center" lineHeight="default" align="center" color={color}>
              {title}
            </H3Text>
            <PlanPriceSection
              countryCode={countryCode}
              isAnnual={isAnnual}
              color={color}
              highlightPrice={!plan.discountedPrice}
              isTrialPeriodEnabled={plan.isTrialPeriodEnabled}
              billingCode={plan.billingCode}
              billed={plan.billed}
              currencyCode={plan.currencyCode}
              planTermCode={plan.term?.code}
              price={Number(plan.price)}
              strikeThroughPrice={Number(plan.strikeThroughPrice)}
            />
          </FlexCol>
        </FlexRow>
        {plan.discountPercent && (
          <Ribbon bgColor="paywallBestDealBg" direction="right" top={2}>
            <H4Text fontWeight="bold">
              {t('common:paywall.plan.discount', { discountPercent: plan.discountPercent })}
            </H4Text>
          </Ribbon>
        )}
      </ImageButton>
    </FlexCol>
  );
};

export interface PlanSmProps extends PlanSelectionProps {
  isCompletePurchase?: boolean;
  setShowPaymentMethods?: (showPaymentMethods: boolean) => void;
  isModal?: boolean;
}

export const PlanSm: FC<PlanSmProps> = React.memo(
  ({
    plans,
    countryCode,
    setSelectedPlan,
    selectedPlan,
    isCompletePurchase,
    setShowPaymentMethods = () => null,
    onBack,
    startingStep,
    onNext,
    paywallLocation,
    isAnnual,
    modalVariant,
    isModal,
  }) => {
    const { t } = useTranslation();
    const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB && isModal;

    const uniqueId = generateUniqueId({ plan: selectedPlan, paywallLocation });
    const goToNextStep = () => {
      if (!selectedPlan?.canUpgrade) {
        return;
      }
      onNext();
    };

    useEffect(() => {
      if (plans?.length && !selectedPlan) {
        setSelectedPlan(plans[plans.length - 1]);
      }
    }, [plans, selectedPlan, setSelectedPlan]);

    const getTitle = (p: PlanType, t: (_: string) => string) => {
      if (p.canRenew) {
        return t(`common:paywall.plan.renewNow`);
      }
      if (p.isCurrent) {
        return t(`common:paywall.plan.currentPlan`);
      }
      return p.title;
    };

    const showablePlans = plans?.filter((p) => !p.shouldHidePlan);

    return (
      <FlexCol w="full" gap="1">
        <GridContainer columns="repeat(2, 1fr)" w="full" justifyContent="center" alignItems="stretch">
          {showablePlans?.map((p) => {
            const isFullWidth = p.code === PlanCode.Ultimate || showablePlans?.length === 1;
            let color: ColorProp = 'default';
            if (p.code === PlanCode.Ultimate) {
              color = selectedPlan?.gradientColor ? 'inverted' : 'default';
            }
            return p.code !== PlanCode.Free ? (
              <BasicContainer
                gridColumnStart={isFullWidth ? 1 : undefined}
                gridColumnEnd={isFullWidth ? -1 : undefined}
                padding={0}
                w="full"
                order={p.code === PlanCode.Ultimate ? 0 : 1}
                key={p.billingCode}
              >
                <PlanSmItem
                  plan={p}
                  setSelectedPlan={setSelectedPlan}
                  selectedPlan={selectedPlan}
                  title={getTitle(p, t)}
                  countryCode={countryCode}
                  isAnnual={isAnnual}
                  color={color}
                  modalVariant={modalVariant}
                />
              </BasicContainer>
            ) : (
              <BasicContainer
                gridColumnStart={1}
                gridColumnEnd={-1}
                padding={0}
                w="full"
                order={showablePlans?.length}
                key={p.billingCode}
              >
                <ImageButton onClick={() => setSelectedPlan(p)} unsetSizing className="cn_w-full cn_atom_p-0">
                  <BasicContainer w="full">
                    <FlexRow
                      wrap="nowrap"
                      w="full"
                      className="cn_atom_p-2"
                      gap="2"
                      justifyContent="space-between"
                      borderColor="paywallVanilla"
                      borderRadius="sm"
                      borderSize={0.1}
                      borderPlacement="all"
                      bgColor={
                        selectedPlan?.billingCode === BillingCodes.registration || p.isCurrent
                          ? 'disabled'
                          : 'transparent'
                      }
                    >
                      <BodyText fontWeight="medium">{p.isCurrent ? p.cta : p.title}</BodyText>
                      <FlexRow justifyContent="flex-end" alignItems="center">
                        <FormattedPriceMonth
                          price={Number(p.price)}
                          planCurrencyCode={p.currencyCode}
                          countryCode={countryCode}
                          planTermCode={p.billingCode}
                          PriceComponent={<H3Text fontWeight="bold"> </H3Text>}
                          LabelComponent={<BodyTextSmall> </BodyTextSmall>}
                        />
                      </FlexRow>
                    </FlexRow>
                  </BasicContainer>
                </ImageButton>
              </BasicContainer>
            );
          })}
        </GridContainer>
        <FeatureList
          temperature={selectedPlan?.temperature}
          features={selectedPlan?.keyFeatures}
          isFullFeatureList
          iconName={selectedPlan?.iconName}
          textColor={isLifestyleUpgradeModal ? 'inverted' : 'default'}
        />
        {selectedPlan?.isTrialPeriodEnabled && (
          <BodyTextSmall color="disabled" align="center" className="cn_atom_pb-3">
            {t('common:paywall.plan.trial.legal.cancelTime', { trialLengthDays: selectedPlan.trialLengthDays ?? 30 })}
          </BodyTextSmall>
        )}
        {isCompletePurchase && (
          <>
            <PrimaryButton
              variant="secondary"
              full
              onClick={() => setShowPaymentMethods(true)}
              label={t('common:button.next')}
            />
            {startingStep !== PAYWALL_STEPS.CHECKOUT && (
              <PrimaryButton full onClick={onBack} label={t('common:button.back')} variant="secondary" />
            )}
          </>
        )}
        {!isCompletePurchase && (
          <>
            <PlanButton
              plan={selectedPlan}
              goToNextStep={goToNextStep}
              t={t}
              uniqueId={uniqueId}
              className={`cn_gradient-bg-${selectedPlan?.gradientColor} cn_mb-3`}
            />
            <PrimaryButton full onClick={onBack} label={t('button.cancel')} variant="secondary" />
          </>
        )}
      </FlexCol>
    );
  }
);
